<template>
	<n-form ref="formRef" :model="form" :rules size="large" class="flex flex-col gap-4">
		<n-tabs
			v-if="showAppointmentTypeTabs"
			v-model:value="appointmentType"
			type="segment"
			animated
			pane-class="p-0!"
		>
			<n-tab-pane name="visit" :tab="$t(`appointments.form.types.visit`)" />
			<n-tab-pane name="other" :tab="$t(`appointments.form.types.other`)" />
		</n-tabs>

		<n-form-item
			path="title"
			:label="$t(`appointments.form.fields.title`)"
			:show-feedback="false"
			:required="!isVisitMode"
		>
			<n-input v-model:value="form.title" :placeholder="$t(`appointments.form.placeholders.title`)" />
		</n-form-item>

		<n-form-item
			v-if="isVisitMode"
			path="patient_id"
			:show-feedback="false"
			:show-require-mark="false"
			class="[&_.n-form-item-label\_\_text]:w-full"
		>
			<template #label>
				<div class="flex w-full items-center justify-between gap-2">
					<span>
						{{ $t(`appointments.form.fields.patient`) }}
						<span class="n-form-item-label__asterisk">*</span>
					</span>
					<JMButton
						v-if="form.patient_id && !initialPatientId"
						type="primary"
						size="tiny"
						text
						class="min-h-0! [&_.n-button\_\_icon]:my-0!"
						icon-name="carbon:launch"
						:icon-size="14"
						icon-placement="right"
						:label="$t(`appointments.form.actions.view_patient`)"
						@click="viewPatient"
					/>
				</div>
			</template>
			<div class="flex w-full flex-col gap-4">
				<PatientSelect
					v-model="form.patient_id"
					:disabled="patientReadonly || !!initialPatientId"
					:placeholder="$t(`appointments.form.placeholders.patient`)"
				/>
				<n-alert type="info">
					Puoi aggiungere appuntamenti solo per le pazienti già registrate. Per creare una nuova paziente
					<RouterLink to="/patients/create">clicca qui.</RouterLink>
				</n-alert>
			</div>
		</n-form-item>

		<!--
			Office: hidden when there is a single office (auto-selected). With no offices we show an alert,
			but only in visit mode where the office is required — in "other" mode we show nothing.
		-->
		<n-form-item
			v-if="offices.length > 1 || (!offices.length && isVisitMode)"
			path="office_id"
			:label="$t(`appointments.form.fields.office`)"
			:show-feedback="false"
			:required="isVisitMode"
		>
			<div class="flex w-full flex-col gap-3">
				<n-alert v-if="!offices.length && !officesLoading" type="warning">
					{{ $t(`appointments.form.no_offices.message`) }}
					<RouterLink :to="createOfficeRoute">
						{{ $t(`appointments.form.no_offices.action`) }}
					</RouterLink>
				</n-alert>

				<OfficeSelect
					v-else-if="offices.length > 1"
					v-model="form.office_id"
					:clearable="!isVisitMode"
					:placeholder="$t(`appointments.form.placeholders.office`)"
				/>
			</div>
		</n-form-item>

		<div v-if="selectedOfficeWorkSchedule" class="bg-secondary flex flex-col gap-0 rounded-lg px-0.5 py-1 text-sm">
			<div class="px-2 py-1 text-xs">{{ $t(`offices.form.fields.work_schedule`) }}</div>

			<WorkScheduleCard :work-schedule="selectedOfficeWorkSchedule" />
		</div>

		<n-alert v-if="workScheduleWarning" type="warning">
			{{ workScheduleWarning }}
		</n-alert>

		<n-divider class="my-3!" />

		<template v-if="isVisitMode">
			<n-form-item path="datetime" :label="$t(`appointments.form.fields.datetime`)" :show-feedback="false">
				<n-date-picker
					v-model:value="form.datetime"
					type="datetime"
					format="dd MMM YYYY, HH:mm"
					class="w-full"
					input-readonly
				/>
			</n-form-item>

			<n-form-item path="duration" :label="$t(`appointments.form.fields.duration`)" :show-feedback="false">
				<DurationInput v-model="form.duration" :placeholder="$t(`appointments.form.placeholders.duration`)" />
			</n-form-item>

			<n-form-item :label="$t(`appointments.form.fields.end_datetime`)" :show-feedback="false">
				<n-input disabled :value="endDatetimeLabel" />
			</n-form-item>
		</template>

		<template v-else>
			<template v-if="otherDateFields === 'range'">
				<n-form-item
					path="endDatetime"
					:show-feedback="false"
					:show-require-mark="false"
					class="[&_.n-form-item-label\_\_text]:w-full"
				>
					<template #label>
						<div class="flex w-full items-center justify-between gap-3">
							<span>
								{{ $t(`common.labels.period`) }}
								<span class="n-form-item-label__asterisk">*</span>
							</span>
							<label class="flex shrink-0 cursor-pointer items-center gap-2">
								<span class="text-secondary text-fs-xs font-normal">
									{{ $t(`appointments.form.fields.all_day`) }}
								</span>
								<n-switch v-model:value="form.allDay" size="small" />
							</label>
						</div>
					</template>
					<n-date-picker
						v-model:value="otherDateRange"
						:type="otherPicker.rangeType"
						:format="otherPicker.format"
						class="w-full"
						input-readonly
					/>
				</n-form-item>
			</template>
			<template v-else>
				<n-form-item
					path="datetime"
					:show-feedback="false"
					:show-require-mark="false"
					class="[&_.n-form-item-label\_\_text]:w-full"
				>
					<template #label>
						<div class="flex w-full items-center justify-between gap-3">
							<span>
								{{ otherStartLabel }}
								<span class="n-form-item-label__asterisk">*</span>
							</span>
							<label class="flex shrink-0 cursor-pointer items-center gap-2">
								<span class="text-secondary text-fs-xs font-normal">
									{{ $t(`appointments.form.fields.all_day`) }}
								</span>
								<n-switch v-model:value="form.allDay" size="small" />
							</label>
						</div>
					</template>
					<n-date-picker
						v-model:value="form.datetime"
						:type="otherPicker.type"
						:format="otherPicker.format"
						class="w-full"
						input-readonly
					/>
				</n-form-item>

				<n-form-item path="endDatetime" :label="otherEndLabel" :show-feedback="false">
					<n-date-picker
						v-model:value="form.endDatetime"
						:type="otherPicker.type"
						:format="otherPicker.format"
						class="w-full"
						input-readonly
					/>
				</n-form-item>
			</template>
		</template>

		<n-form-item v-if="showDraftField" :show-feedback="false" :show-label="false">
			<n-checkbox v-model:checked="form.draft">
				{{ $t(`appointments.form.fields.draft`) }}
			</n-checkbox>
		</n-form-item>

		<n-alert v-if="hasOverlap" type="warning">
			{{ $t(`appointments.form.availability.overlap`) }}
			<template v-if="formattedSuggestions.length">
				{{ $t(`appointments.form.availability.suggestions`) }}
			</template>

			<div v-if="formattedSuggestions.length" class="mt-3 flex flex-col gap-4">
				<div v-for="day in formattedSuggestions" :key="day.date">
					<div class="border-divider mb-2 flex items-baseline justify-between gap-2 border-b pb-2">
						<span class="text-fs-sm font-semibold capitalize">
							{{ day.weekday }}
						</span>
						<span class="text-secondary text-fs-xs font-medium">
							{{ day.label }}
						</span>
					</div>

					<div class="flex flex-wrap gap-1.5">
						<JMButton
							v-for="slot in day.slots"
							:key="slot.from"
							type="secondary"
							size="tiny"
							@click="selectSuggestedSlot(slot)"
						>
							<span class="text-secondary">{{ slot.fromLabel }}</span>
							<span class="text-tertiary mx-1">–</span>
							<span class="text-secondary">{{ slot.toLabel }}</span>
						</JMButton>
					</div>
				</div>
			</div>
		</n-alert>
	</n-form>
</template>

<script setup lang="ts">
import type { FormInst, FormItemRule, FormRules } from "naive-ui"
import type { AppointmentPayload } from "@/api/endpoints/common/appointments"
import type { I18nString } from "@/lang/config"
import type { Appointment, AppointmentFormType, AppointmentSuggestedSlots } from "@/types/appointment"
import type { DateTimeString } from "@/types/common"
import { useDebounceFn } from "@vueuse/core"
import {
	NAlert,
	NCheckbox,
	NDatePicker,
	NDivider,
	NForm,
	NFormItem,
	NInput,
	NSwitch,
	NTabPane,
	NTabs,
	useDialog,
	useMessage
} from "naive-ui"
import { storeToRefs } from "pinia"
import { computed, nextTick, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import JMButton from "@/components/common/JMButton.vue"
import OfficeSelect from "@/components/offices/OfficeSelect.vue"
import { getWorkScheduleViolation, hasWorkSchedule } from "@/components/offices/utils"
import WorkScheduleCard from "@/components/offices/WorkScheduleCard.vue"
import PatientSelect from "@/components/patient/PatientSelect.vue"
import DurationInput from "@/components/profile/Settings/DurationInput.vue"
import { useNavigation } from "@/composables/router/useNavigation"
import { useAuthStore } from "@/stores/auth"
import { useLocalesStore } from "@/stores/i18n"
import { useOfficesStore } from "@/stores/offices"
import { confirmDialog, getApiErrorMessage } from "@/utils"
import { allDayDurationToDayCount, dayCountToAllDayDuration, formatSuggestedDays, startOfDayMs } from "@/utils/calendar"
import dayjs, { dFormat, dPayload } from "@/utils/dayjs"

export type OtherDateFields = "separate" | "range"

const props = withDefaults(
	defineProps<{
		active?: boolean
		appointment?: Appointment | null
		initialDatetime?: number | null
		initialEndDatetime?: number | null
		initialAppointmentType?: AppointmentFormType | null
		initialAllDay?: boolean | null
		initialDuration?: number | null
		initialOfficeId?: number | null
		initialPatientId?: number | null
		patientReadonly?: boolean
		showDraftField?: boolean
		showAppointmentTypeTabs?: boolean
		otherDateFields?: OtherDateFields
	}>(),
	{
		active: false,
		appointment: null,
		initialDatetime: null,
		initialEndDatetime: null,
		initialAppointmentType: null,
		initialAllDay: null,
		initialDuration: null,
		initialOfficeId: null,
		initialPatientId: null,
		patientReadonly: false,
		showDraftField: false,
		showAppointmentTypeTabs: true,
		otherDateFields: "range"
	}
)

const emit = defineEmits<{
	saved: []
	deleted: []
}>()

const AVAILABILITY_DEBOUNCE_MS = 400
const DT_PARSE = "YYYY-MM-DD HH:mm:ss"

const dialog = useDialog()
const message = useMessage()
const { t } = useI18n()
const authStore = useAuthStore()
const localesStore = useLocalesStore()
const { routePatients } = useNavigation()
const officesStore = useOfficesStore()
const { list: offices, loading: officesLoading } = storeToRefs(officesStore)

const createOfficeRoute = {
	name: "Profile",
	query: {
		profile_active_tab: JSON.stringify("offices"),
		office_action: "create"
	}
}

const formRef = ref<FormInst | null>(null)
const saving = ref(false)
const deleting = ref(false)
const appointmentType = ref<AppointmentFormType>("visit")
const hasOverlap = ref(false)
const suggestedSlots = shallowRef<AppointmentSuggestedSlots[]>([])
/** Blocks availability checks while the form is being hydrated from props. */
const availabilityEnabled = ref(false)
let availabilityRequestId = 0

const form = ref(createEmptyForm())

const isVisitMode = computed(() => appointmentType.value === "visit")
const formattedSuggestions = computed(() => formatSuggestedDays(suggestedSlots.value, localesStore.locale))

const otherPicker = computed(() =>
	form.value.allDay
		? { type: "date" as const, rangeType: "daterange" as const, format: "dd MMM YYYY" }
		: { type: "datetime" as const, rangeType: "datetimerange" as const, format: "dd MMM YYYY, HH:mm" }
)

const otherStartLabel = computed(() =>
	tt(form.value.allDay ? `appointments.form.fields.start_date` : `appointments.form.fields.start_datetime`)
)
const otherEndLabel = computed(() =>
	tt(form.value.allDay ? `appointments.form.fields.end_date` : `appointments.form.fields.end_datetime_full`)
)

const otherDateRange = computed<[number, number] | null>({
	get(): [number, number] | null {
		if (form.value.datetime == null) return null
		return [form.value.datetime, form.value.endDatetime ?? form.value.datetime]
	},
	set(value: [number, number] | null) {
		form.value.datetime = value?.[0] ?? null
		form.value.endDatetime = value?.[1] ?? null
		if (value && form.value.allDay) normalizeOtherModeDates()
	}
})

const endDatetimeLabel = computed(() => {
	if (!form.value.datetime || !form.value.duration) return "—"
	return dFormat(addMinutes(form.value.datetime, form.value.duration), "datetime")
})

const selectedOfficeWorkSchedule = computed(() => {
	const office = offices.value.find(item => item.id === form.value.office_id)
	return office && hasWorkSchedule(office.work_schedule) ? office.work_schedule : null
})

/**
 * Warns (without blocking the save) when the appointment falls on a closing day
 * or outside the working hours of the selected office.
 */
const workScheduleWarning = computed(() => {
	const workSchedule = selectedOfficeWorkSchedule.value
	const start = form.value.datetime

	if (!workSchedule || start == null) return null

	const end = isVisitMode.value ? endFromStart(start) : form.value.endDatetime

	// All-day appointments (and ranges without an end) have no meaningful hours to compare
	if (end == null || (!isVisitMode.value && form.value.allDay)) return null

	const violation = getWorkScheduleViolation(workSchedule, start, end)

	return violation ? tt(`appointments.form.availability.${violation}`) : null
})

const rules = computed<FormRules>(() => {
	const datetime = requiredNumber(`appointments.form.validators.datetime_required`)

	const visitRules: FormRules = {
		datetime,
		duration: requiredNumber(`appointments.form.validators.duration_required`, ["blur", "change", "input"]),
		patient_id: requiredNumber(`appointments.form.validators.patient_required`),
		office_id: requiredNumber(`appointments.form.validators.office_required`)
	}

	const otherRules: FormRules = {
		datetime,
		endDatetime: {
			required: true,
			type: "number",
			trigger: ["blur", "change"],
			validator(_rule, value: number | null) {
				if (value == null) return new Error(tt(`appointments.form.validators.end_datetime_required`))
				if (form.value.datetime == null) return true

				const invalid = form.value.allDay
					? dayjs(value).isBefore(dayjs(form.value.datetime), "day")
					: value <= form.value.datetime

				if (!invalid) return true

				return new Error(
					tt(
						form.value.allDay
							? `appointments.form.validators.end_date_invalid`
							: `appointments.form.validators.end_datetime_invalid`
					)
				)
			}
		},
		title: {
			required: true,
			trigger: ["blur", "input"],
			validator: (_rule: FormItemRule, value: string) =>
				value?.trim() ? true : new Error(tt(`appointments.form.validators.title_required`))
		}
	}

	return isVisitMode.value ? visitRules : otherRules
})

function tt(key: I18nString) {
	return t<I18nString>(key)
}

function requiredNumber(messageKey: I18nString, trigger: FormItemRule["trigger"] = ["blur", "change"]): FormItemRule {
	return {
		required: true,
		type: "number",
		message: tt(messageKey),
		trigger
	}
}

function addMinutes(start: number, minutes: number) {
	return dayjs(start).add(minutes, "minute").valueOf()
}

function effectiveDuration() {
	return form.value.duration || authStore.avgVisitDuration
}

function endFromStart(start: number) {
	return addMinutes(start, effectiveDuration())
}

function createEmptyForm() {
	return {
		datetime: Date.now() as number | null,
		endDatetime: null as number | null,
		duration: authStore.avgVisitDuration,
		title: "",
		patient_id: null as number | null,
		office_id: officesStore.resolveOfficeId(props.initialOfficeId) as number | null,
		draft: false,
		allDay: false
	}
}

function getDurationFromRange(start: number, end: number) {
	const minutes = dayjs(end).diff(dayjs(start), "minute")
	return minutes > 0 ? minutes : authStore.avgVisitDuration
}

function getAllDayDurationMinutes(start: number, end: number) {
	// Inclusive picker range: same calendar day → 1 day.
	const dayCount = dayjs(end).startOf("day").diff(dayjs(start).startOf("day"), "day") + 1
	return dayCountToAllDayDuration(dayCount)
}

function rangeDuration(start: number, end: number, allDay = form.value.allDay) {
	return allDay ? getAllDayDurationMinutes(start, end) : getDurationFromRange(start, end)
}

function normalizeOtherModeDates() {
	if (!form.value.allDay || form.value.datetime == null) return

	form.value.datetime = startOfDayMs(form.value.datetime)

	if (form.value.endDatetime == null) return

	form.value.endDatetime = startOfDayMs(form.value.endDatetime)

	if (dayjs(form.value.endDatetime).isBefore(dayjs(form.value.datetime), "day")) {
		form.value.endDatetime = form.value.datetime
	}
}

function resetForm() {
	appointmentType.value = "visit"
	form.value = createEmptyForm()
}

function fillFormFromAppointment(value: Appointment) {
	const start = dayjs(value.datetime, DT_PARSE).valueOf()
	const duration = value.duration ?? authStore.avgVisitDuration
	const isAllDay = value.type === "OTHER" && value.all_day
	const startMs = isAllDay ? startOfDayMs(start) : start

	appointmentType.value = value.type === "OTHER" ? "other" : "visit"
	form.value = {
		datetime: startMs,
		endDatetime: isAllDay
			? dayjs(startMs)
					.add(allDayDurationToDayCount(duration) - 1, "day")
					.valueOf()
			: addMinutes(start, duration),
		duration,
		title: value.title,
		patient_id: value.patient?.id ?? null,
		office_id: value.office?.id ?? null,
		draft: value.draft,
		allDay: isAllDay
	}
}

/**
 * With a single office the picker is hidden, so the selection is driven by the mode:
 * visits are bound to that office, "other" appointments are left without one.
 */
function syncSingleOfficeSelection() {
	if (offices.value.length !== 1) return

	form.value.office_id = isVisitMode.value ? (offices.value[0]?.id ?? null) : null
}

function viewPatient() {
	if (!form.value.patient_id) return
	routePatients({ patientId: form.value.patient_id }).navigate()
}

function resolveAvailabilityDuration(): number | null {
	if (isVisitMode.value) {
		return form.value.duration > 0 ? form.value.duration : null
	}

	const { datetime: start, endDatetime: end } = form.value
	if (start == null || end == null) return null

	return rangeDuration(start, end)
}

function clearAvailabilityState() {
	hasOverlap.value = false
	suggestedSlots.value = []
}

function checkAvailability() {
	if (!props.active || !availabilityEnabled.value) return

	const slot = form.value.datetime
	const duration = resolveAvailabilityDuration()

	if (slot == null || duration == null || duration <= 0) {
		clearAvailabilityState()
		return
	}

	const requestId = ++availabilityRequestId

	Api.common.appointments
		.checkAvailability({
			slot: new Date(slot),
			duration,
			...(props.appointment ? { exclude_appointment_id: props.appointment.id } : {}),
			...(form.value.office_id != null ? { office_id: form.value.office_id } : {})
		})
		.then(res => {
			if (requestId !== availabilityRequestId) return
			const overlap = !!res.data?.overlap
			hasOverlap.value = overlap
			suggestedSlots.value = overlap ? (res.data?.suggestions ?? []) : []
		})
		.catch(() => {
			if (requestId !== availabilityRequestId) return
			clearAvailabilityState()
		})
}

const debouncedCheckAvailability = useDebounceFn(checkAvailability, AVAILABILITY_DEBOUNCE_MS)

function selectSuggestedSlot(slot: { from: DateTimeString; to: DateTimeString }) {
	const from = dayjs(slot.from, DT_PARSE).valueOf()
	const to = dayjs(slot.to, DT_PARSE).valueOf()

	form.value.allDay = false
	form.value.datetime = from
	form.value.duration = Math.max(dayjs(to).diff(dayjs(from), "minute"), 1)

	if (!isVisitMode.value) {
		form.value.endDatetime = to
	}
}

function notifyApiError(err: Parameters<typeof getApiErrorMessage>[0]) {
	message.error(getApiErrorMessage(err) || tt(`common.misc.generic_error`))
}

watch(
	() => form.value.allDay,
	allDay => {
		if (isVisitMode.value) return

		if (allDay) {
			normalizeOtherModeDates()
			form.value.endDatetime ??= form.value.datetime
			return
		}

		const start = Date.now()
		form.value.datetime = start
		form.value.endDatetime = endFromStart(start)
		formRef.value?.restoreValidation()
	}
)

watch(
	() => [form.value.datetime, form.value.endDatetime] as const,
	() => {
		if (form.value.allDay && !isVisitMode.value) normalizeOtherModeDates()
	}
)

watch([appointmentType, () => offices.value.length], () => syncSingleOfficeSelection())

watch(appointmentType, (mode, previousMode) => {
	if (mode === previousMode || !form.value.datetime) return

	if (mode === "other") {
		form.value.endDatetime ??= endFromStart(form.value.datetime)
		normalizeOtherModeDates()
		return
	}

	form.value.allDay = false

	if (form.value.endDatetime) {
		form.value.duration = getDurationFromRange(form.value.datetime, form.value.endDatetime)
	}

	formRef.value?.restoreValidation()
})

watch(
	() =>
		[
			props.active,
			props.appointment,
			props.initialDatetime,
			props.initialEndDatetime,
			props.initialAppointmentType,
			props.initialAllDay,
			props.initialDuration,
			props.initialOfficeId,
			props.initialPatientId
		] as const,
	([isOpen, currentAppointment, datetime, endDatetime, type, allDay, duration, , patientId]) => {
		if (!isOpen) {
			availabilityEnabled.value = false
			clearAvailabilityState()
			return
		}

		availabilityEnabled.value = false
		clearAvailabilityState()

		if (currentAppointment) {
			fillFormFromAppointment(currentAppointment)
		} else {
			resetForm()
			if (datetime) form.value.datetime = datetime
			if (endDatetime != null) form.value.endDatetime = endDatetime
			if (allDay != null) form.value.allDay = allDay
			if (duration != null) form.value.duration = duration
			if (patientId != null) form.value.patient_id = patientId
			if (type) appointmentType.value = type
			if (type === "other" && allDay) normalizeOtherModeDates()
		}

		syncSingleOfficeSelection()

		nextTick(() => {
			availabilityEnabled.value = true
			if (!currentAppointment) checkAvailability()
		})
	},
	{ immediate: true }
)

watch(
	() =>
		[
			form.value.datetime,
			form.value.endDatetime,
			form.value.duration,
			form.value.allDay,
			form.value.office_id,
			appointmentType.value
		] as const,
	() => {
		if (props.active && availabilityEnabled.value) debouncedCheckAvailability()
	}
)

function buildPayload(): AppointmentPayload {
	const title = form.value.title ? form.value.title.trim() : ""
	const draft = props.showDraftField ? form.value.draft : false

	if (isVisitMode.value) {
		return {
			type: "VISIT",
			all_day: false,
			datetime: dPayload(form.value.datetime!, "datetime"),
			duration: form.value.duration,
			title,
			draft,
			patient_id: form.value.patient_id!,
			office_id: form.value.office_id!
		}
	}

	let start = form.value.datetime!
	let end = form.value.endDatetime!

	if (form.value.allDay) {
		start = startOfDayMs(start)
		end = startOfDayMs(end)
	}

	return {
		type: "OTHER",
		all_day: form.value.allDay,
		datetime: dPayload(start, "datetime"),
		duration: rangeDuration(start, end),
		title,
		draft,
		...(form.value.office_id != null ? { office_id: form.value.office_id } : {})
	}
}

function submit() {
	formRef.value?.validate(errors => {
		if (errors) return

		saving.value = true
		const payload = buildPayload()
		const request = props.appointment
			? Api.common.appointments.update(props.appointment.id, payload)
			: Api.common.appointments.create(payload)

		request
			.then(() => {
				message.success(
					tt(props.appointment ? `appointments.form.messages.updated` : `appointments.form.messages.created`)
				)
				emit("saved")
			})
			.catch(notifyApiError)
			.finally(() => {
				saving.value = false
			})
	})
}

function deleteAppointment() {
	if (!props.appointment) return

	confirmDialog(dialog, {
		title: tt(`appointments.common.delete_appointment`),
		content: tt(`appointments.common.delete_confirm`),
		positiveText: tt(`common.misc.confirm`),
		negativeText: tt(`common.misc.cancel`),
		success() {
			deleting.value = true

			Api.common.appointments
				.delete(props.appointment!.id)
				.then(() => {
					message.success(tt(`appointments.form.messages.deleted`))
					emit("deleted")
				})
				.catch(notifyApiError)
				.finally(() => {
					deleting.value = false
				})
		}
	})
}

defineExpose({
	submit,
	deleteAppointment,
	resetForm,
	saving,
	deleting,
	appointmentType
})
</script>
